import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getTenants, updateTenant, impersonate, deleteTenant } from '../../services/api';
import { safeParseFeatures } from '../../context/AuthContext';
import { ALL_FEATURES } from './MasterTenantForm';
import { useConfirm } from '../../components/ConfirmDialog';

function useCopy() {
  const [copied, setCopied] = useState({});
  const copy = (key, value) => {
    navigator.clipboard.writeText(value)
      .then(() => { setCopied(p => ({...p, [key]: true})); setTimeout(() => setCopied(p => ({...p, [key]: false})), 2000); })
      .catch(() => toast.error('Copiar manualmente'));
  };
  return { copied, copy };
}

// Labels curtos para o card da listagem. Reaproveita o registry oficial
// (ALL_FEATURES em MasterTenantForm) com overrides curtos para economizar
// espaço no card mobile. Qualquer feature nova ganha label automaticamente.
const FEAT_SHORT_OVERRIDE = {
  appointments:    'Agenda',
  billing:         'Faturamento',
  notifications:   'Notificações',
  public_booking:  'Booking público',
  google_calendar: 'Google Calendar',
  whatsapp:        'WhatsApp',
  google_review:   'Avaliação Google',
  qr_code:         'QR Code',
  reschedule:      'Reagendamento',
  waitlist:        'Lista de espera',
  client_profiles: 'Perfil de clientes',
  nps:             'Satisfação (NPS)',
  intake_forms:    'Perguntas',
  recurring:       'Recorrência',
  deposits:        'Sinal (PIX)',
  loyalty:         'Pontos',
  packages:        'Pacotes',
};
const featLabel = (k) => FEAT_SHORT_OVERRIDE[k] || ALL_FEATURES[k]?.label || k;
const STATUS_BADGE = { active:'badge-success', suspended:'badge-warning', trial:'badge-info', deleted:'badge-gray' };
const STATUS_LABEL = { active:'Ativo', suspended:'Suspenso', trial:'Trial', deleted:'Excluído' };

export default function MasterTenants() {
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  // Default "Ativos" — esconde suspensos/trial automaticamente. Quem quiser
  // ver todos ou só suspensos troca manualmente no seletor.
  const [statusF,  setStatusF]  = useState('active');
  const [toggling, setToggling] = useState({});
  const [deleting, setDeleting] = useState({});
  const { copied, copy } = useCopy();
  const confirm = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    getTenants({ search, status: statusF })
      .then(r => setTenants(r.data.tenants))
      .catch(() => toast.error('Erro ao carregar clientes'))
      .finally(() => setLoading(false));
  }, [search, statusF]);

  useEffect(() => { load(); }, [load]);

  const doImpersonate = async id => {
    try {
      const res = await impersonate(id);
      if (res.data.token) {
        localStorage.setItem('master_token', res.data.token);
      }
      window.location.href = '/app';
    } catch { toast.error('Erro ao acessar cliente'); }
  };

  // Suspender/reativar: mudança reversível de status. NÃO cascateia —
  // faturas, usuários, agendamentos ficam intactos. Uso típico:
  // cliente inadimplente, quando paga volta a 'active' sem perder nada.
  const toggleStatus = async t => {
    const ns = t.status === 'active' ? 'suspended' : 'active';
    setToggling(p => ({ ...p, [t.id]: true }));
    try {
      await updateTenant(t.id, { status: ns });
      toast.success(`Cliente ${ns === 'active' ? 'reativado' : 'suspenso'}!`);
      load();
    } catch { toast.error('Erro ao alterar status'); }
    finally { setToggling(p => ({ ...p, [t.id]: false })); }
  };

  // Excluir: ação IRREVERSÍVEL com cascata.
  //   - tenant.status = 'deleted'
  //   - usuários desativados
  //   - agendamentos futuros cancelados
  //   - faturas pendentes canceladas (pagas ficam como histórico)
  //   - integrações desativadas
  //   - cliente some de toda contabilização do sistema
  //   - para voltar: recadastro manual do zero
  const doDelete = async t => {
    const ok = await confirm({
      title: `Excluir "${t.name}"?`,
      message: 'Esta ação é IRREVERSÍVEL. Ao excluir o cliente:',
      items: [
        'Ele some de todas as listagens e métricas',
        'Todos os usuários são desativados',
        'Agendamentos futuros são cancelados',
        'Faturas pendentes são canceladas',
        'Ele NÃO poderá voltar a ser ativo — só recadastrando do zero',
      ],
      hint: 'Se a ideia é bloquear temporariamente (ex.: inadimplência), use Suspender — preserva todos os dados e pode ser revertido com 1 clique.',
      confirmText: 'Sim, excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!ok) return;

    setDeleting(p => ({ ...p, [t.id]: true }));
    try {
      await deleteTenant(t.id);
      toast.success(`Cliente "${t.name}" excluído.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir cliente');
    } finally {
      setDeleting(p => ({ ...p, [t.id]: false }));
    }
  };

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header"><h2>Clientes</h2><p>{tenants.length} cliente(s)</p></div>
        <Link to="/master/tenants/new" className="btn btn-primary">+ Novo Cliente</Link>
      </div>

      <div className="filter-bar">
        <input className="search-input" placeholder="Buscar nome, email ou slug..." value={search}
          onChange={e => setSearch(e.target.value)} />
        <select value={statusF} onChange={e => setStatusF(e.target.value)} style={{ maxWidth:160 }}>
          <option value="active">Ativos</option>
          <option value="suspended">Suspensos</option>
          <option value="trial">Trial</option>
          <option value="">Ativos + Suspensos + Trial</option>
          <option value="deleted">Excluídos</option>
        </select>
        {(search || statusF !== 'active') && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatusF('active'); }}>✕ Limpar</button>
        )}
      </div>

      {loading ? <div className="spinner" style={{ margin:'40px auto', display:'block' }} /> : (
        tenants.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🏢</div><h3>Nenhum cliente encontrado</h3></div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap:16, marginTop:8 }}>
            {tenants.map(t => {
              const f = safeParseFeatures(t.features);
              const activeFeats = Object.entries(f).filter(([,v]) => v).map(([k]) => k);
              const initial = (t.name || 'A')[0].toUpperCase();
              return (
                <div key={t.id} className="mobile-card" style={{ background:'var(--white)', borderRadius:12, border:'1px solid var(--gray-200)', padding:16, display:'flex', flexDirection:'column', gap:12, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                  {/* Header: logo/initial + name + status */}
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {t.logo_url
                      ? <img src={t.logo_url} alt="" style={{ width:40, height:40, borderRadius:8, objectFit:'cover', flexShrink:0 }} />
                      : <div style={{ width:40, height:40, borderRadius:8, background: t.primary_color || 'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'white', fontSize:16, flexShrink:0 }}>{initial}</div>
                    }
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:15, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</div>
                    </div>
                    <span className={`badge ${STATUS_BADGE[t.status] || 'badge-gray'}`} style={{ flexShrink:0 }}>{STATUS_LABEL[t.status] || t.status}</span>
                  </div>

                  {/* Slug + plan */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <code className="slug" style={{ fontSize:12, background:'var(--gray-100)', padding:'2px 7px', borderRadius:5 }}>{t.slug}</code>
                    {t.plan_name && <span className="badge badge-info" style={{ fontSize:11 }}>{t.plan_name}</span>}
                  </div>

                  {/* Features */}
                  {activeFeats.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                      {activeFeats.map(k => (
                        <span key={k} className="feature-tag" style={{ fontSize:11 }}>{featLabel(k)}</span>
                      ))}
                    </div>
                  )}

                  {/* Share links */}
                  {(() => {
                    const origin   = window.location.origin;
                    const loginUrl = `${origin}/login?tenant=${encodeURIComponent(t.slug)}`;
                    const script   = t.widget_token ? `<script src="${origin}/api/widget/${t.widget_token}/embed.js" async></script>` : null;
                    return (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', padding:'8px 0', borderTop:'1px solid var(--gray-100)' }}>
                        <button
                          className={`btn btn-xs ${copied[`link-${t.id}`] ? 'btn-success' : 'btn-secondary'}`}
                          style={{ flex:1 }}
                          onClick={() => copy(`link-${t.id}`, loginUrl)}
                          title={loginUrl}>
                          {copied[`link-${t.id}`] ? '✓ Copiado' : '🔑 Link de Login'}
                        </button>
                        {script && (
                          <button
                            className={`btn btn-xs ${copied[`widget-${t.id}`] ? 'btn-success' : 'btn-secondary'}`}
                            style={{ flex:1 }}
                            onClick={() => copy(`widget-${t.id}`, script)}
                            title="Script para inserir no site">
                            {copied[`widget-${t.id}`] ? '✓ Copiado' : '📦 Widget'}
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  {t.status === 'deleted' ? (
                    // Cliente excluído: apenas leitura. Sem botões de ação —
                    // pra reativar tem que recadastrar do zero.
                    <div style={{ paddingTop:4, borderTop:'1px solid var(--gray-100)', fontSize:12, color:'var(--gray-500)', textAlign:'center', fontStyle:'italic' }}>
                      Cliente excluído — recadastre para reativar
                    </div>
                  ) : (
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4, borderTop:'1px solid var(--gray-100)' }}>
                      <button className="btn btn-xs btn-primary" style={{ flex:1, minWidth:70 }} onClick={() => doImpersonate(t.id)}>Acessar</button>
                      <Link to={`/master/tenants/${t.id}`} className="btn btn-xs btn-secondary" style={{ flex:1, minWidth:60, textAlign:'center' }}>Ver / Editar</Link>
                      <button
                        className={`btn btn-xs ${t.status === 'active' ? 'btn-warning' : 'btn-success'}${toggling[t.id] ? ' btn-loading' : ''}`}
                        style={{ flex:1, minWidth:70 }}
                        onClick={() => toggleStatus(t)}
                        disabled={toggling[t.id]}
                        title={t.status === 'active' ? 'Bloqueio temporário (reversível)' : 'Reativar cliente'}>
                        {toggling[t.id] ? '' : t.status === 'active' ? 'Suspender' : 'Reativar'}
                      </button>
                      <button
                        className={`btn btn-xs btn-danger${deleting[t.id] ? ' btn-loading' : ''}`}
                        style={{ flex:1, minWidth:60 }}
                        onClick={() => doDelete(t)}
                        disabled={deleting[t.id]}
                        title="Exclusão irreversível — recadastro necessário para voltar">
                        {deleting[t.id] ? '' : 'Excluir'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
